import { isAxiosError } from 'axios'
import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { logger } from '@app/utils/logger'
import type { paths } from '@src/types/api'
import { ApiCollection, Folder } from '../collections.types'

export type CreateFolderPayload = NonNullable<
  paths['/evidence/collections']['post']['requestBody']
>['content']['application/json']
export type UpdateFolderPayload = NonNullable<
  paths['/evidence/collections/{collection_id}']['patch']['requestBody']
>['content']['application/json']

export function isDuplicateFolderNameError(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 409 &&
    (error.response?.data as { code?: string } | undefined)?.code === 'DUPLICATE_NAME'
  )
}

function mapApiCollection(collection: ApiCollection): Folder {
  return {
    id: collection.uuid,
    name: collection.name,
    description: collection.description,
    createdBy: collection.created_by,
    createdByEmail: collection.created_by_email,
    createdAt: collection.created_at,
    updatedAt: collection.updated_at,
    documentCount: collection.document_count,
  }
}

export async function fetchFolders(): Promise<Folder[]> {
  try {
    const { data } = await apiClient.get<
      paths['/evidence/collections']['get']['responses'][200]['content']['application/json']
    >(Endpoint.collections)
    return data.collections.map(mapApiCollection)
  } catch (error) {
    logger.error('Error fetching folders:', error)
    throw error
  }
}

export async function createFolder(payload: CreateFolderPayload): Promise<Folder> {
  try {
    const { data } = await apiClient.post<ApiCollection>(Endpoint.collections, payload)
    return mapApiCollection(data)
  } catch (error) {
    logger.error('Error creating folder:', error)
    throw error
  }
}

export async function updateFolder(id: string, payload: UpdateFolderPayload): Promise<Folder> {
  try {
    const { data } = await apiClient.patch<ApiCollection>(`${Endpoint.collections}/${id}`, payload)
    return mapApiCollection(data)
  } catch (error) {
    logger.error('Error updating folder:', error)
    throw error
  }
}

export async function deleteFolder(id: string): Promise<void> {
  try {
    await apiClient.delete<void>(`${Endpoint.collections}/${id}`)
  } catch (error) {
    logger.error('Error deleting folder:', error)
    throw error
  }
}

// A document can only be in one folder, so this moves it if it's already in another.
export async function addDocumentToFolder(folderId: string, documentId: string): Promise<void> {
  try {
    await apiClient.post<void>(`${Endpoint.collections}/${folderId}/documents/${documentId}`)
  } catch (error) {
    logger.error('Error adding document to folder:', error)
    throw error
  }
}

export async function removeDocumentFromFolder(
  folderId: string,
  documentId: string,
): Promise<void> {
  try {
    await apiClient.delete<void>(`${Endpoint.collections}/${folderId}/documents/${documentId}`)
  } catch (error) {
    logger.error('Error removing document from folder:', error)
    throw error
  }
}
